import styled from '@emotion/styled';
import {
  AdAnalyticsFilter,
  AdAnalyticsMedia,
  AdAnalyticsPlacement,
  AdAnalyticsPlacementTypes,
  AdAnalyticsPlatform,
  PerformanceReportPeriodPattern,
} from '@kaizenplatform/hugot-entity';
import { FormControl, InputLabel, MenuItem, Select, Typography } from '@material-ui/core';
import { Chip as _Chip } from '@material-ui/core';
import { createElement, FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { SPACING_REGULAR } from '../../constant/spacing';

type Props = {
  onUpdatePlacementTypes: ({ platformKey, mediaKey, placementKey, period }: AdAnalyticsFilter) => void;
  placementTypes: AdAnalyticsPlacementTypes | null;
  selectedPlacementTypes: AdAnalyticsFilter;
  className?: string;
};

const Filters: FC<Props> = ({
  placementTypes,
  selectedPlacementTypes,
  onUpdatePlacementTypes,
  className,
}: {
  onUpdatePlacementTypes: ({ platformKey, mediaKey, placementKey, period }: AdAnalyticsFilter) => void;
  placementTypes: AdAnalyticsPlacementTypes | null;
  selectedPlacementTypes: AdAnalyticsFilter;
  className?: string;
}) => {
  if (!placementTypes) {
    return null;
  }
  const periods = placementTypes.periods;

  // platform一覧を取得
  let platformKeys: AdAnalyticsPlatform[] = [];
  placementTypes.platforms.map((platform) => platformKeys.push(platform));
  platformKeys = Array.from(new Set(platformKeys));

  // 選択されたplatformで絞り込んだmedia一覧を表示
  const selectedPlatform = selectedPlacementTypes;
  let mediaKeys: AdAnalyticsMedia[] = [];
  const availablePlatforms = placementTypes.platforms.filter(
    (platform) =>
      selectedPlatform.platformKey !== undefined && selectedPlatform.platformKey.indexOf(platform.key) !== -1,
  );
  availablePlatforms.map(
    (platform) => platform.networks !== undefined && platform.networks.map((media) => mediaKeys.push(media)),
  );
  mediaKeys = Array.from(new Set(mediaKeys));

  // 選択されたmediaで絞り込んだplacement一覧を表示.
  const selectedMedia = selectedPlacementTypes.mediaKey;
  let placementKeys: AdAnalyticsPlacement[] = [];
  availablePlatforms.map(
    (platform) =>
      platform.networks !== undefined &&
      platform.networks
        .filter((media) => selectedMedia !== undefined && selectedMedia.indexOf(media.key) !== -1)
        .map(
          (media) =>
            media.placements !== undefined && media.placements.map((placement) => placementKeys.push(placement)),
        ),
  );
  placementKeys = Array.from(new Set(placementKeys));

  const handleChangePlatforms = (e: React.ChangeEvent<{ value: any }>) => {
    if (e.target.value.length) {
      onUpdatePlacementTypes({
        platformKey: e.target.value,
        mediaKey: [],
        placementKey: [],
      });
    } else {
      onUpdatePlacementTypes({
        platformKey: [],
        mediaKey: [],
        placementKey: [],
      });
    }
  };

  const handleChangeMedias = (e: React.ChangeEvent<{ value: any }>) => {
    if (e.target.value.length) {
      onUpdatePlacementTypes({
        mediaKey: e.target.value,
        placementKey: [],
      });
    } else {
      onUpdatePlacementTypes({
        mediaKey: [],
        placementKey: [],
      });
    }
  };

  const handleCHangePlacements = (e: React.ChangeEvent<{ value: any }>) => {
    onUpdatePlacementTypes({
      placementKey: e.target.value,
    });
  };

  return (
    <Root className={className}>
      <Selector>
        <InputLabel>
          <FormattedMessage id="adAnalytics_detail.label_platform" />
        </InputLabel>
        <Select
          multiple={true}
          name="platform"
          value={selectedPlacementTypes.platformKey !== undefined ? selectedPlacementTypes.platformKey : []}
          renderValue={(selected) => (
            <Chips>
              {(selected as string[]).map((value) => (
                <Chip key={value} label={platformKeys.filter((platform) => platform.key === value)[0].name} />
              ))}
            </Chips>
          )}
          onChange={handleChangePlatforms}
          displayEmpty
        >
          {platformKeys.map((platformKey, index) => (
            <SizedMenuItem value={platformKey.key} key={`PlatformSelector-${platformKey.key}${index}`}>
              <Typography>{platformKey.name}</Typography>
            </SizedMenuItem>
          ))}
        </Select>
      </Selector>

      <Selector>
        <InputLabel>
          <FormattedMessage id="adAnalytics_detail.label_media" />
        </InputLabel>
        <Select
          multiple={true}
          name="media"
          value={selectedPlacementTypes.mediaKey !== undefined ? selectedPlacementTypes.mediaKey : []}
          renderValue={(selected) => (
            <Chips>
              {(selected as string[]).map((value) => (
                <Chip key={value} label={mediaKeys.filter((media) => media.key === value)[0].name} />
              ))}
            </Chips>
          )}
          onChange={handleChangeMedias}
          displayEmpty
          disabled={mediaKeys.length ? false : true}
        >
          {mediaKeys.map((mediaKey, index) => (
            <SizedMenuItem value={mediaKey.key} key={`MediaSelector-${mediaKey.key}${index}`}>
              <Typography>{mediaKey.name}</Typography>
            </SizedMenuItem>
          ))}
        </Select>
      </Selector>

      <Selector>
        <InputLabel>
          <FormattedMessage id="adAnalytics_detail.label_placement" />
        </InputLabel>
        <Select
          multiple={true}
          name="placement"
          value={selectedPlacementTypes.placementKey !== undefined ? selectedPlacementTypes.placementKey : []}
          renderValue={(selected) => (
            <Chips>
              {(selected as string[]).map((value) => (
                <Chip key={value} label={placementKeys.filter((placement) => placement.key === value)[0].name} />
              ))}
            </Chips>
          )}
          onChange={handleCHangePlacements}
          displayEmpty
          disabled={placementKeys.length ? false : true}
        >
          {placementKeys.map((placementKey, index) => (
            <SizedMenuItem value={placementKey.key} key={`PlacementSelector-${placementKey.key}${index}`}>
              <Typography>{placementKey.name}</Typography>
            </SizedMenuItem>
          ))}
        </Select>
      </Selector>

      <Selector>
        <InputLabel>
          <FormattedMessage id="adAnalytics_detail.label_period" />
        </InputLabel>
        <Select
          name="period"
          value={selectedPlacementTypes.period}
          onChange={(event) => {
            onUpdatePlacementTypes({
              period: event.target.value as PerformanceReportPeriodPattern,
            });
          }}
          displayEmpty
        >
          {periods.map((period, index) => (
            <SizedMenuItem value={period} key={`PeriodSelector-${period}${index}`}>
              <Typography>
                <FormattedMessage id={`adAnalytics_detail.select_value.${period}`} />
              </Typography>
            </SizedMenuItem>
          ))}
        </Select>
      </Selector>
    </Root>
  );
};

const Root = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  maxWidth: '80%',
});

const Selector = styled(FormControl)({
  width: 185,
  marginLeft: SPACING_REGULAR,
});

const SizedMenuItem = styled(MenuItem)({
  maxWidth: 360,
});

const Chip = styled(_Chip)({
  margin: '2px',
});

const Chips = styled('div')({
  display: 'flex',
  flexWrap: 'wrap',
});

export default Filters;
